import type { IColor4 } from "@base/util/Color4";
import { PartialRecord, Primitive, TupleToUnion } from "@base/util/TypeUtil";
import { IBeosCompany, IBeosUser } from "@biz/definitions/DatabaseDefinitions";
import { MarkerType } from "@biz/definitions/MapsDefinitions";

export const SOLIDAY_COMPANY_ID = 22137;

export const colors: { [key: string]: IColor4 } = {
  orange: { r: 247 / 255.0, g: 166 / 255.0, b: 0 / 255.0, a: 1.0 },
  grey: { r: 93 / 255.0, g: 105 / 255.0, b: 110 / 255.0, a: 1.0 },
  darkGrey: { r: 46 / 255.0, g: 52 / 255.0, b: 55 / 255.0, a: 1.0 },
  yellow: { r: 255 / 255.0, g: 221 / 255.0, b: 0 / 255.0, a: 1.0 },
};

export type Unit = "" | "Stk" | "m" | "lfm" | "mm" | "m2";

export interface ILastModified {
  _lastModified: number;
}

export const sizes = {
  topBarHeight: 80,
  bottomBarHeight: 32,
};

export const svgSunSailAspect = 16 / 9;

export type ToInterface<T> = {
  [P in keyof T]: T[P];
};

export enum LanguageCodeNumber {
  Invalid = 0,
  de_AT = 1,
  en_GB = 2,
  fr_FR = 3,
  it_IT = 4,
  es_ES = 5,
}

export enum ErrorCode {
  Exception = -1,
  Parse = 101,
  Query = 102,
  Parameters = 103,
  Login = 104,
  Device = 105,
  Compress = 106,
  OutOfDate = 107,
  Webservice = 108,
  Access = 109,
  AppError = 1000,
}

export type PropertyAttributes<T, A> = Partial<
  {
    [P in keyof T]: A;
  }
>;

export type PropertyNames<T> = PropertyAttributes<T, string>;

export type MimeType = "image/jpeg" | "image/png" | "image/webp" | "video/mp4" | "application/pdf" | "text/html" | "text/vtt" | "";
export interface IFileInfo {
  name: string; // File
  size: number;
  lastModified: number; // File
  mimeType: MimeType;
  hash?: string;
  url: string;
  // serverPath: string;
}

export interface IUploadServerFileResult {
  id: string;
  sourceIndex: number; // index of uploaded file
  info: IFileInfo;
  success: boolean;
  generatedFiles?: Record<string, IFileInfo>; // thumbs...
}

export interface IDetailedCompany {
  _id: number;
  company: IBeosCompany;
  users: IBeosUser[];
  markerType: MarkerType;
  sales: number[];
}

export const LanguageTypeValues = ["de_AT", "en_GB", "fr_FR", "it_IT", "es_ES"] as const;
export type LanguageType = TupleToUnion<typeof LanguageTypeValues>;

export type LocalizedType<T> = PartialRecord<LanguageType, T>;

export interface ILanguageDbItem extends PartialRecord<LanguageType | "bundle", string>, ILastModified {
  _id: string;
}

// const __ENV = {
//   debug: true,
//   isServer: false,
//   buildTime: 0
// }

export type SceneType = "scene" | "offer" | "order";
export type OrderType = "H" | "S" | "HS";
export type ConfiguratorType = "2d" | "3d" | "sc";

export interface IScenesPageParams {
  isDealer: boolean;
  sceneType: SceneType;
  type: "app" | "sunsail" | "both";
}

// export function mapPriceListIndex(priceListIndex: number | undefined): number {
//   if (priceListIndex === undefined) return 0;
//   if (priceListIndex === 5) return 1;
//   return 0;
// }

export interface ILoginToken {
  userName: string;
  deviceId: string;
  timestamp: number;
}

export interface IRequestInfo {
  userName: string;
  timestamp: Date;
  baseUrl: string;
  method: string;
  refreshTokenUserName: string | undefined;
  appType: string;
  ip: string;
  buildTime: string;
  host: string;
  query: string;
  body: string;
  statusCode: number;
  logs?: string;
}

export interface IPropertySelectItem<T extends Primitive = string> {
  value: T | undefined;
  label: string | React.ReactNode;
  disabled?: boolean;
}

export const beosCountryCodes = {
  "A": { flag: "at" },
  "D": { flag: "de" },
  "B": { flag: "be" },
  "E": { flag: "es" },
  "S": { flag: "se" },
  "L": { flag: "lu" },
  "H": { flag: "hu" },
  "SLO": { flag: "si" },
  "I": { flag: "it" },
  "N": { flag: "no" },
  "F": { flag: "fr" },
  "SRB": { flag: "rs" },
  "AND": { flag: "ad" },
  "NZE": { flag: "nz" },
  "SF": { flag: "fi" },
  "IRL": { flag: "ie" },
  "DOM": { flag: "do" },
  "RUS": { flag: "ru" },
  "UAE": { flag: "ae" },
  "IND": { flag: "in" },
  "P": { flag: "pt" },
  "LI": { flag: "lb" },
  "USA": { flag: "us" },
}

export type BeosCountryCode = keyof typeof beosCountryCodes;

export type PartListGroup = "Teile" | "Allgemein" | "Motor" | "Welle" | "Warenkorb" | "Sonderkonsole" | "Vorgefertigt" | "All-In-One"
  | "Vormontage" | "Tuchprofile" | "Profilzuschnitt Raff-C" | "Führungsschiene Raff-C mit Antrieb" | "Motoreinheit Raff-C" | "Seilzug" | "Zubehör Raff-C" | "Zubehör Raff-M" | "Profilzuschnitt Raff-M" | "Zubehör Raincut" | "Zubehör Materialzugabe" | "System Verpackung";

export const SYSTEM_PACKAGING = "System Verpackung";